import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { attendanceService, employeeService } from "../../../services/api";
import { ModuleActionsFrame } from "../shared/ModuleActionsFrame";
import ModuleActionEmployeePanel from "../shared/ModuleActionEmployeePanel";
import { ATTENDANCE_ACTION_ICONS } from "../payroll/payrollActionChrome";

/**
 * Hub action key → RBAC screen under Attendance → Actions.
 * `run` = permission needed to Process/Open (view-only actions use "view").
 */
export const ATTENDANCE_ACTION_RBAC = {
  change_status: { screen: "att_act_change_status", run: "edit" },
  apply_leave: { screen: "att_act_apply_leave", run: "create" },
  change_shift: { screen: "att_act_change_shift", run: "edit" },
  change_wo: { screen: "att_act_change_wo", run: "edit" },
  regularise: { screen: "att_act_regularise_checks", run: "create" },
  short_leave: { screen: "att_act_short_leave", run: "create" },
  lop_reversal: { screen: "att_act_lop_reversal", run: "edit" },
  leave_adjustment: { screen: "att_act_leave_adjustment", run: "edit" },
  leave_balance: { screen: "att_act_leave_balance", run: "view" },
  comp_off_ledger: { screen: "att_act_comp_off_ledger", run: "view" },
  view_edit_checks: { screen: "view_edit_checks", run: "view" },
  on_duty: { screen: "att_act_on_duty", run: "create" },
  overtime: { screen: "att_act_overtime", run: "create" },
  restricted_holiday: { screen: "att_act_restricted_holiday", run: "create" },
};

const ACTION_DEFS = [
  {
    key: "change_status",
    label: "Change Status",
    blurb: "Open attendance calendar (Change Status) for the selected employee.",
    openLabel: "Open",
    handler: "onChangeStatus",
    listTitle: "Status list",
    emptyList: "No Change Status actioned employees yet.",
    processHint:
      "Select the employee whose day status you want to change. Status list shows employees already actioned via Change Status.",
  },
  {
    key: "apply_leave",
    label: "Apply Leave",
    blurb: "Apply leave for the selected employee.",
    openLabel: "Apply",
    handler: "onApplyLeave",
    listTitle: "Leave list",
    emptyList: "No leave requests yet.",
    processHint:
      "Select the employee applying for leave. Leave list shows employees with leave requests already raised.",
  },
  {
    key: "change_shift",
    label: "Change Shift",
    blurb: "Change shift for the selected employee.",
    openLabel: "Change",
    handler: "onChangeShift",
    listTitle: "Shift list",
    emptyList: "No Change Shift requests yet.",
    processHint:
      "Select the employee whose shift needs to be updated. Shift list shows employees with Change Shift requests.",
  },
  {
    key: "change_wo",
    label: "Change WO",
    blurb: "Change weekly off for the selected employee.",
    openLabel: "Change",
    handler: "onChangeWo",
    listTitle: "WO list",
    emptyList: "No Change WO requests yet.",
    processHint:
      "Weekly-off changes respect attendance locks for the selected month. WO list shows employees with Change WO requests.",
  },
  {
    key: "regularise",
    label: "Regularise checks",
    blurb: "Regularise attendance checks for the selected employee.",
    openLabel: "Regularise",
    handler: "onRegulariseChecks",
    listTitle: "Regularise list",
    emptyList: "No regularisation requests yet.",
    processHint:
      "Select the employee whose punches need regularisation. Regularise list shows employees already actioned.",
  },
  {
    key: "short_leave",
    label: "Short Leave",
    blurb: "Apply short leave for the selected employee.",
    openLabel: "Apply",
    handler: "onShortLeave",
    listTitle: "Short leave list",
    emptyList: "No short leave requests yet.",
    processHint:
      "Select the employee for short leave. Short leave list shows employees with short leave requests.",
  },
  {
    key: "lop_reversal",
    label: "LOP Reversal",
    blurb: "Reverse LOP for the selected employee.",
    openLabel: "Reverse",
    handler: "onLopReversal",
    listTitle: "LOP list",
    emptyList: "No LOP reversals recorded yet.",
    processHint:
      "LOP reversal may still be allowed in locked months when policy permits. LOP list shows employees with reversals already done.",
  },
  {
    key: "leave_adjustment",
    label: "Leave Adjustment",
    blurb: "Adjust leave balance for the selected employee.",
    openLabel: "Adjust",
    handler: "onLeaveAdjustment",
    listTitle: "Adjustment list",
    emptyList: "No leave adjustments recorded yet.",
    processHint:
      "Select the employee for leave adjustment. Adjustment list shows employees with leave adjustments already recorded.",
  },
  {
    key: "leave_balance",
    label: "Leave Balance",
    blurb: "View leave balance for the selected employee.",
    openLabel: "View",
    handler: "onLeaveBalance",
    listTitle: "Balance list",
    emptyList: "No leave-balance action history yet — use Process to open an employee.",
    processHint:
      "Select the employee to view leave balances. Balance list stays empty until a dedicated history is stored.",
  },
  {
    key: "comp_off_ledger",
    label: "Comp Off Ledger",
    blurb: "View Comp Off lots for the selected employee.",
    openLabel: "Open",
    handler: "onCompOffLedger",
    listTitle: "Comp Off list",
    emptyList: "No Comp Off ledger rows yet.",
    processHint:
      "Select the employee to open Comp Off ledger. Comp Off list shows employees who already have ledger lots.",
  },
  {
    key: "view_edit_checks",
    label: "View and Edit Checks",
    blurb: "View or edit all punches and recalc status.",
    openLabel: "Open",
    handler: "onViewEditChecks",
    listTitle: "Checks list",
    emptyList: "No View and Edit Checks actioned employees yet.",
    processHint:
      "Select the employee whose punches you want to view or edit. Checks list shows employees already edited via this action.",
  },
  {
    key: "on_duty",
    label: "On Duty",
    blurb: "Raise On Duty for the selected employee.",
    openLabel: "Open",
    handler: "onOdRequest",
    listTitle: "On Duty list",
    emptyList: "No On Duty requests yet.",
    processHint:
      "Select the employee for On Duty request. On Duty list shows employees with OD requests already raised.",
  },
  {
    key: "overtime",
    label: "Overtime",
    blurb: "Raise Overtime for the selected employee.",
    openLabel: "Open",
    handler: "onOtRequest",
    listTitle: "Overtime list",
    emptyList: "No Overtime records yet.",
    processHint:
      "Select the employee for Overtime request. Overtime list shows employees with OT already recorded.",
  },
  {
    key: "restricted_holiday",
    label: "Restricted Holiday",
    blurb: "Raise Restricted Holiday for the selected employee.",
    openLabel: "Open",
    handler: "onRhRequest",
    listTitle: "RH list",
    emptyList: "No Restricted Holiday selections yet.",
    processHint:
      "Select the employee for Restricted Holiday. RH list shows employees with RH selections already recorded.",
  },
];

/**
 * Attendance Actions — Process | actioned List (same framework as Payroll Actions).
 * Visibility and Process/Open are gated per action via Attendance → Actions screens.
 *
 * @param {(screenKey: string, action?: string) => boolean} canActionScreen
 */
export default function AttendanceActionsHub({
  darkMode = false,
  employees = [],
  canActionScreen = () => false,
  onChangeStatus = null,
  onApplyLeave = null,
  onChangeShift = null,
  onChangeWo = null,
  onRegulariseChecks = null,
  onShortLeave = null,
  onLopReversal = null,
  onLeaveAdjustment = null,
  onLeaveBalance = null,
  onCompOffLedger = null,
  onViewEditChecks = null,
  onOdRequest = null,
  onOtRequest = null,
  onRhRequest = null,
}) {
  const handlers = {
    onChangeStatus,
    onApplyLeave,
    onChangeShift,
    onChangeWo,
    onRegulariseChecks,
    onShortLeave,
    onLopReversal,
    onLeaveAdjustment,
    onLeaveBalance,
    onCompOffLedger,
    onViewEditChecks,
    onOdRequest,
    onOtRequest,
    onRhRequest,
  };

  const visibleActions = useMemo(() => {
    return ACTION_DEFS.filter((a) => {
      const rbac = ATTENDANCE_ACTION_RBAC[a.key];
      if (!rbac) return false;
      if (!canActionScreen(rbac.screen, "view")) return false;
      return typeof handlers[a.handler] === "function";
    });
    // handlers object is rebuilt each render; depend on individual handler props
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canActionScreen,
    onChangeStatus,
    onApplyLeave,
    onChangeShift,
    onChangeWo,
    onRegulariseChecks,
    onShortLeave,
    onLopReversal,
    onLeaveAdjustment,
    onLeaveBalance,
    onCompOffLedger,
    onViewEditChecks,
    onOdRequest,
    onOtRequest,
    onRhRequest,
  ]);

  const [activeKey, setActiveKey] = useState(() => visibleActions[0]?.key || "");
  const [directory, setDirectory] = useState(() =>
    Array.isArray(employees) ? employees : []
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visibleActions.length) return;
    if (!visibleActions.some((a) => a.key === activeKey)) {
      setActiveKey(visibleActions[0].key);
    }
  }, [visibleActions, activeKey]);

  const loadDirectory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await employeeService.getEmployees({
        page: 1,
        limit: 400,
        excludeEmploymentTypes: "External,external",
      });
      let list = [];
      if (Array.isArray(response?.data)) list = response.data;
      else if (Array.isArray(response?.employees)) list = response.employees;
      setDirectory(list.length ? list : Array.isArray(employees) ? employees : []);
    } catch (err) {
      console.error("AttendanceActionsHub load:", err);
      setDirectory(Array.isArray(employees) ? employees : []);
    } finally {
      setLoading(false);
    }
  }, [employees]);

  useEffect(() => {
    loadDirectory();
  }, [loadDirectory]);

  const active = visibleActions.find((a) => a.key === activeKey) || visibleActions[0];
  const activeRbac = active ? ATTENDANCE_ACTION_RBAC[active.key] : null;
  const canRunActive =
    !!activeRbac && canActionScreen(activeRbac.screen, activeRbac.run || "view");

  if (!visibleActions.length) {
    return (
      <p className="mt-6 text-center text-sm text-[hsl(var(--ds-muted-fg))]">
        No attendance actions available for your role.
      </p>
    );
  }

  return (
    <ModuleActionsFrame
      actions={visibleActions}
      activeKey={active?.key}
      onSelect={setActiveKey}
      icons={ATTENDANCE_ACTION_ICONS}
    >
      <ModuleActionEmployeePanel
        key={active?.key}
        darkMode={darkMode}
        actionKey={active?.key}
        title={active?.label}
        blurb={active?.blurb}
        processHint={active?.processHint || ""}
        listTitle={active?.listTitle || "Actioned list"}
        emptyList={active?.emptyList || "No actioned employees yet."}
        openLabel={active?.openLabel || "Continue"}
        Icon3d={ATTENDANCE_ACTION_ICONS[active?.key]}
        employees={directory}
        loadingEmployees={loading}
        listActionRecords={attendanceService.listActionRecords}
        onRefreshEmployees={loadDirectory}
        onOpenEmployee={(emp) => {
          if (!canRunActive) {
            toast.error(
              `You can see ${active?.label || "this action"} but do not have permission to process it.`
            );
            return;
          }
          const fn = handlers[active?.handler];
          if (typeof fn === "function") fn(emp);
        }}
      />
    </ModuleActionsFrame>
  );
}
